import {
  ButtonType,
  CovButton,
  CovIcon,
  CovUploader,
  DragDropFileSelectError,
  IconName,
  MimeType,
} from "@cx-component-library/react-components";
import { Box, Stack, Typography } from "@mui/material";
import { type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import "./PreviewImageUploader.css";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_FORMATS = [MimeType.JPEG, MimeType.JPG, MimeType.PNG];
const ALLOWED_EXTENSIONS = ".jpg,.jpeg,.png";

const ERROR_KEYS: Record<DragDropFileSelectError, string> = {
  [DragDropFileSelectError.MAX_FILE_SIZE]: "src_image_uploader_too_large",
  [DragDropFileSelectError.UN_SUPPORTED_FORMAT]:
    "src_image_uploader_unsupported",
  [DragDropFileSelectError.MAX_FILES_EXCEEDED]: "src_image_uploader_one_only",
};

export interface PreviewImageUploaderProps {
  /** URL of the currently selected image, if any. */
  previewUrl?: string;
  onFileSelected: (file: File) => void;
  onRemove: () => void;
  /** `alt` text for the filled preview `<img>`. */
  previewAlt: string;
  /** Caption shown in the empty drop zone, e.g. "JPG, PNG · Max 5MB · Optional". */
  helperText: ReactNode;
  /**
   * Automation hook for the filled state's "Change Image" input
   * (`${testId}-change-input`). The pinned `CovUploader` (4.20) has no
   * `testId` or real file input yet, so the empty drop zone is untagged.
   */
  testId?: string;
}

/**
 * Single-file JPG/PNG (≤ 5 MB) drop zone with a filled/empty toggle, ported
 * from Customer Lounge: `CovUploader`'s drag/browse state when empty, or an
 * `<img>` with "Change Image" / "Remove" once a file is picked.
 */
export const PreviewImageUploader = ({
  previewUrl,
  onFileSelected,
  onRemove,
  previewAlt,
  helperText,
  testId,
}: PreviewImageUploaderProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<DragDropFileSelectError>();
  const inputId = useId();

  const select = (file: File) => {
    setError(undefined);
    onFileSelected(file);
  };

  if (previewUrl) {
    return (
      <Stack sx={{ gap: "var(--spacing12, 12px)", width: "100%" }}>
        <Box
          sx={{
            height: "120px",
            width: "100%",
            border: "1px solid var(--alias-divider-on-white, #d9d9d9)",
            backgroundColor: "var(--alias-surface-low, #f2f2f2)",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={previewUrl}
            alt={previewAlt}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              pointerEvents: "none",
            }}
          />
        </Box>
        <Stack direction="row" sx={{ gap: "var(--spacing12, 12px)" }}>
          <CovButton
            buttonType={ButtonType.TERTIARY_OUTLINE}
            contentLeft={<CovIcon iconName={IconName.EDIT} iconSize="xs" />}
            onClick={() => document.getElementById(inputId)?.click()}
          >
            {t("src_image_uploader_change")}
          </CovButton>
          <CovButton
            buttonType={ButtonType.TERTIARY_OUTLINE}
            contentLeft={<CovIcon iconName={IconName.CLOSE} iconSize="xs" />}
            onClick={() => {
              setError(undefined);
              onRemove();
            }}
          >
            {t("src_image_uploader_remove")}
          </CovButton>
          <input
            id={inputId}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            hidden
            data-testid={testId ? `${testId}-change-input` : undefined}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                select(file);
              }
              event.target.value = "";
            }}
          />
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack sx={{ gap: "var(--spacing8, 8px)", width: "100%" }}>
      <CovUploader
        className="preview-image-uploader"
        maxFileSize={MAX_FILE_SIZE_BYTES}
        allowedFormats={ALLOWED_FORMATS}
        allowedFileExtensions={ALLOWED_EXTENSIONS}
        textContent={
          <Typography
            component="span"
            sx={{ fontSize: "var(--body-b2-14-regular-size, 14px)" }}
          >
            {helperText}
          </Typography>
        }
        onFilesDropped={(files) => {
          const file = files[0];
          if (file) {
            select(file);
          }
        }}
        onFileError={setError}
      />
      {error !== undefined && (
        <Typography
          sx={{
            // No 11px type token in the foundation.
            fontSize: "11px",
            color: "var(--alias-text-on-semantics-red-primary, #e50000)",
            textAlign: "center",
          }}
        >
          {t(ERROR_KEYS[error])}
        </Typography>
      )}
    </Stack>
  );
};
